'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { SPARTAN_PRODUCT_FEATURE_CAROUSEL_LABELS } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/constants';
import { SpartanProductFeatureMediaImage } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-media';
import styles from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/thumbnail-list/styles.module.css';
import type { SpartanProductFeatureThumbnailListHandle, SpartanProductFeatureThumbnailListProps } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/thumbnail-list/types';
import type { SpartanProductFeatureProgressMode } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/types';
import { SPARTAN_PRODUCT_COMPACT_LIST_GAP_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/list-gap';
import { SpartanMediaPlaybackIcon } from '@/app/lcv/components/sections/spartan/products/shared/media-playback-icon';
import { getSpartanRevealGroupProps, getSpartanRevealItemProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE, SPARTAN_REVEAL_SCROLL_OFFSET } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';
const PROGRESS_PATH_D = 'M 20 1 H 31 A 8 8 0 0 1 39 9 V 31 A 8 8 0 0 1 31 39 H 9 A 8 8 0 0 1 1 31 V 9 A 8 8 0 0 1 9 1 H 20 Z';
const updateProgressPath = (path: SVGPathElement, progress: number, isActive: boolean, progressMode: SpartanProductFeatureProgressMode) => {
  const elapsed = progress * 100;
  const visible = progressMode === 'fill' ? elapsed : 100 - elapsed;
  path.style.strokeDasharray = `${visible} 100`;
  path.style.strokeDashoffset = progressMode === 'deplete' ? String(-elapsed) : '0';
  path.style.opacity = isActive && (progressMode === 'fill' || progress < 1) ? '1' : '0';
};
export const SpartanProductFeatureThumbnailList = t0 => {
  const $ = _c(54);
  const {
    ref,
    features,
    tracking,
    dataGASection,
    selectedIndex,
    thumbnailOverflows: t1,
    clipViewport: t2,
    isPlaybackEligible,
    isImagePlaybackActive,
    isVideoPlaybackActive,
    isVideoStarting,
    progressMode,
    emblaRef,
    canSelectFeature,
    onSelectSlide,
    onTogglePlayback
  } = t0;
  const thumbnailOverflows = t1 === undefined ? false : t1;
  const clipViewport = t2 === undefined ? true : t2;
  const selectedIndexRef = useRef(selectedIndex);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const progressPathRefs = useRef(t3);
  const progressValueRef = useRef(0);
  let t4;
  if ($[1] !== progressMode) {
    t4 = (index, value) => {
      if (index !== selectedIndexRef.current) {
        return;
      }
      const progress = Math.min(1, Math.max(0, value));
      progressValueRef.current = progress;
      const progressPath = progressPathRefs.current[index];
      if (!progressPath) {
        return;
      }
      updateProgressPath(progressPath, progress, true, progressMode);
    };
    $[1] = progressMode;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const updateProgress = t4;
  let t5;
  let t6;
  if ($[3] !== updateProgress) {
    t5 = () => ({
      updateProgress
    });
    t6 = [updateProgress];
    $[3] = updateProgress;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useImperativeHandle(ref, t5, t6);
  let t7;
  if ($[6] !== progressMode) {
    t7 = element => {
      if (!element) {
        return;
      }
      const index_0 = Number(element.dataset.progressIndex);
      if (!Number.isInteger(index_0)) {
        return;
      }
      progressPathRefs.current[index_0] = element;
      const isActive = index_0 === selectedIndexRef.current;
      const progress_0 = isActive ? progressValueRef.current : 0;
      updateProgressPath(element, progress_0, isActive, progressMode);
    };
    $[6] = progressMode;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const setProgressPathRef = t7;
  let t8;
  let t9;
  if ($[8] !== progressMode || $[9] !== selectedIndex) {
    t8 = () => {
      if (selectedIndexRef.current !== selectedIndex) {
        selectedIndexRef.current = selectedIndex;
        progressValueRef.current = 0;
      }
      progressPathRefs.current.forEach((path, index_1) => {
        if (!path) {
          return;
        }
        const isActive_0 = index_1 === selectedIndex;
        const progress_1 = isActive_0 ? progressValueRef.current : 0;
        updateProgressPath(path, progress_1, isActive_0, progressMode);
      });
    };
    t9 = [progressMode, selectedIndex];
    $[8] = progressMode;
    $[9] = selectedIndex;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useLayoutEffect(t8, t9);
  const t10 = clipViewport && "overflow-hidden";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("mt-4 w-full max-w-full md:mt-6", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = getSpartanRevealGroupProps(SPARTAN_REVEAL_PRESET.galleryThumbnails, {
      scrollOffset: SPARTAN_REVEAL_SCROLL_OFFSET[SPARTAN_REVEAL_PRESET.galleryThumbnails]
    });
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = !thumbnailOverflows && "justify-center";
  let t14;
  if ($[15] !== t13) {
    t14 = cn("flex touch-pan-y", SPARTAN_PRODUCT_COMPACT_LIST_GAP_CLASS_NAME, t13);
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== tracking.featureThumbnailList) {
    t15 = createDataAttributes({
      test: tracking.featureThumbnailList
    });
    $[17] = tracking.featureThumbnailList;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== canSelectFeature || $[20] !== dataGASection || $[21] !== features || $[22] !== isImagePlaybackActive || $[23] !== isPlaybackEligible || $[24] !== isVideoPlaybackActive || $[25] !== isVideoStarting || $[26] !== onSelectSlide || $[27] !== onTogglePlayback || $[28] !== progressMode || $[29] !== selectedIndex || $[30] !== setProgressPathRef || $[31] !== tracking) {
    let t17;
    if ($[33] !== canSelectFeature || $[34] !== dataGASection || $[35] !== isImagePlaybackActive || $[36] !== isPlaybackEligible || $[37] !== isVideoPlaybackActive || $[38] !== isVideoStarting || $[39] !== onSelectSlide || $[40] !== onTogglePlayback || $[41] !== progressMode || $[42] !== selectedIndex || $[43] !== setProgressPathRef || $[44] !== tracking) {
      t17 = (feature, index_2) => {
        const isSelected = index_2 === selectedIndex;
        const canNavigateToFeature = canSelectFeature(index_2);
        const isSelectable = !isSelected && canNavigateToFeature;
        const isPlaying = isSelected && (feature.video?.desktop.src ? isVideoPlaybackActive : isImagePlaybackActive);
        const isStarting = isSelected && !!feature.video?.desktop.src && isVideoStarting;
        const isPlaybackRequested = isPlaybackEligible && (isPlaying || isStarting);
        const playbackLabel = isPlaybackRequested ? SPARTAN_PRODUCT_FEATURE_CAROUSEL_LABELS.pauseFeature : SPARTAN_PRODUCT_FEATURE_CAROUSEL_LABELS.playFeature;
        const isPlaybackButtonEnabled = isSelected && isPlaybackEligible;
        return <li key={[feature.title, index_2].join("-")} className="w-full max-w-18 min-w-0 shrink-0 md:w-32 md:max-w-32" {...createDataAttributes({
          active: isSelected ? "true" : null
        })} {...getSpartanRevealItemProps({
          order: index_2,
          role: SPARTAN_REVEAL_ROLE.galleryThumbnail,
          staggerMs: 50
        })}><div className="group relative rounded-sm"><button type="button" disabled={isSelected} tabIndex={isSelected ? -1 : 0} aria-disabled={isSelected || !canNavigateToFeature} aria-hidden={isSelected} className={cn("relative block w-full overflow-hidden rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-gray-800 focus-visible:ring-offset-2", {
              "pointer-events-none cursor-default": isSelected,
              "cursor-pointer": isSelectable,
              "cursor-default": !isSelected && !isSelectable,
              [styles.selectable]: isSelectable
            })} onClick={() => {
              if (!canNavigateToFeature) {
                return;
              }
              onSelectSlide(index_2);
            }} aria-label={stripHtmlTags(feature.title)} {...createDataAttributes({
              test: tracking.featureThumbnail(index_2)
            })} {...createDataGA({
              eventName: SPARTAN_PRODUCTS_EVENT_NAME,
              group: dataGASection,
              click: true,
              text: feature.title,
              value: `thumbnail-${index_2 + 1}`
            })}><SpartanProductFeatureMediaImage image={feature.image} alt={feature.title} variant="thumbnail" imgClassName={styles.image} fade={false} testId={tracking.featureThumbnailImage(index_2)} /></button><span aria-hidden="true" className={cn("pointer-events-none absolute inset-0 rounded-sm bg-black/50 transition-opacity duration-300 ease-in-out motion-reduce:transition-none", {
              "opacity-100": isSelected,
              "opacity-0": !isSelected
            })} /><span aria-hidden="true" className={cn("pointer-events-none absolute inset-0 rounded-sm bg-black/25 opacity-0 transition-opacity duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none", {
              "group-hover:opacity-100 group-has-focus-visible:opacity-100 pointer-coarse:group-hover:opacity-0": isSelectable
            })} /><span className="pointer-events-none absolute inset-0 flex items-center justify-center"><button type="button" disabled={!isSelected || !isPlaybackEligible} tabIndex={isPlaybackButtonEnabled ? 0 : -1} aria-hidden={!isSelected} className={cn("relative flex size-full items-center justify-center rounded-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800", {
                "pointer-events-auto cursor-pointer": isPlaybackButtonEnabled,
                "pointer-events-none": !isSelected || !isPlaybackEligible
              })} onClick={onTogglePlayback} aria-label={`${playbackLabel}: ${stripHtmlTags(feature.title)}`} {...createDataAttributes({
                test: tracking.featureThumbnailPlayback(index_2)
              })} {...createDataGA({
                eventName: SPARTAN_PRODUCTS_EVENT_NAME,
                group: dataGASection,
                click: true,
                text: playbackLabel,
                value: `${isPlaybackRequested ? "pause" : "play"}-thumbnail-${index_2 + 1}`
              })}><svg aria-hidden="true" viewBox="0 0 40 40" className="pointer-events-none absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-1/2"><path d={PROGRESS_PATH_D} pathLength="100" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeDasharray={progressMode === "fill" ? "0 100" : "100 100"} strokeDashoffset="0" opacity={isSelected ? 1 : 0} data-progress-index={index_2} ref={setProgressPathRef} /></svg><SpartanMediaPlaybackIcon isPlaying={isPlaybackRequested} isVisible={isSelected} className="size-5" /></button></span></div></li>;
      };
      $[33] = canSelectFeature;
      $[34] = dataGASection;
      $[35] = isImagePlaybackActive;
      $[36] = isPlaybackEligible;
      $[37] = isVideoPlaybackActive;
      $[38] = isVideoStarting;
      $[39] = onSelectSlide;
      $[40] = onTogglePlayback;
      $[41] = progressMode;
      $[42] = selectedIndex;
      $[43] = setProgressPathRef;
      $[44] = tracking;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    t16 = features.map(t17);
    $[19] = canSelectFeature;
    $[20] = dataGASection;
    $[21] = features;
    $[22] = isImagePlaybackActive;
    $[23] = isPlaybackEligible;
    $[24] = isVideoPlaybackActive;
    $[25] = isVideoStarting;
    $[26] = onSelectSlide;
    $[27] = onTogglePlayback;
    $[28] = progressMode;
    $[29] = selectedIndex;
    $[30] = setProgressPathRef;
    $[31] = tracking;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[46] !== t14 || $[47] !== t15 || $[48] !== t16) {
    t17 = <ul className={t14} {...t15}>{t16}</ul>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== emblaRef || $[51] !== t11 || $[52] !== t17) {
    t18 = <div ref={emblaRef} className={t11} role="region" aria-roledescription="carousel" aria-label={SPARTAN_PRODUCT_FEATURE_CAROUSEL_LABELS.featureThumbnails} {...t12}>{t17}</div>;
    $[50] = emblaRef;
    $[51] = t11;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};